import {
    DEDUCTIBLE_FIELD_LABELS,
    DeductibleAmountField,
    FINANCE_CALC_TYPES,
    NO_ALLOCATION,
    NO_ALLOCATION_LABEL,
    fieldsForCalcType,
    isFinanceCalcType,
} from "@features/admin/finance/financeCalcTypes";
import {
    DeductibleConfig,
    DeductibleConfigInput,
    DeductibleEditorRow,
    DeductibleEditorValue,
    DeductibleRowErrors,
    DeductibleRowState,
} from "@features/admin/finance/types/deductibleConfig";

export interface DeductibleCatalogItem {
    deductible_id: number;
    name: string;
    list_order: number;
    is_active: boolean;
}

const AMOUNT_FIELDS: DeductibleAmountField[] = ["rate", "floor_amount", "cap_amount", "fixed_amount"];

export const EMPTY_ROW_STATE: DeductibleRowState = {
    calc_type: NO_ALLOCATION,
    rate: "",
    floor_amount: "",
    cap_amount: "",
    fixed_amount: "",
};

const byListOrder = (a: { list_order: number; deductible_id: number }, b: { list_order: number; deductible_id: number }) =>
    a.list_order - b.list_order || a.deductible_id - b.deductible_id;

/**
 * Rows for the editor: every active deductible, plus (when editing) inactive deductibles the
 * version already configures. Inactive rows are locked.
 */
export function buildEditorRows(catalog: DeductibleCatalogItem[], configs: DeductibleConfig[] = []): DeductibleEditorRow[] {
    const rows = new Map<number, DeductibleEditorRow>();
    for (const d of catalog) {
        if (!d.is_active) continue;
        rows.set(d.deductible_id, { deductible_id: d.deductible_id, name: d.name, is_active: true, list_order: d.list_order, locked: false });
    }
    for (const c of configs) {
        if (rows.has(c.deductible_id)) continue;
        const catalogItem = catalog.find((d) => d.deductible_id === c.deductible_id);
        const isActive = catalogItem ? catalogItem.is_active : c.is_active;
        rows.set(c.deductible_id, {
            deductible_id: c.deductible_id,
            name: catalogItem?.name ?? c.name,
            is_active: isActive,
            list_order: catalogItem?.list_order ?? c.list_order,
            locked: !isActive,
        });
    }
    return Array.from(rows.values()).sort(byListOrder);
}

const toInput = (value: string | number | null | undefined): string =>
    value === null || value === undefined ? "" : String(value);

/** Editor state for an existing configuration. */
export function editorValueFromConfigs(configs: DeductibleConfig[] = []): DeductibleEditorValue {
    const value: DeductibleEditorValue = {};
    for (const c of configs) {
        value[c.deductible_id] = {
            calc_type: c.calc_type,
            rate: toInput(c.rate),
            floor_amount: toInput(c.floor_amount),
            cap_amount: toInput(c.cap_amount),
            fixed_amount: toInput(c.fixed_amount),
        };
    }
    return value;
}

/**
 * Save payload: one entry per editable row with a calc type. Locked (inactive) rows are left out
 * because the server carries their configuration forward unchanged.
 */
export function buildDeductiblePayload(rows: DeductibleEditorRow[], value: DeductibleEditorValue): DeductibleConfigInput[] {
    const payload: DeductibleConfigInput[] = [];
    for (const row of rows) {
        if (row.locked) continue;
        const state = value[row.deductible_id];
        if (!state || !isFinanceCalcType(state.calc_type)) continue;
        const fields = fieldsForCalcType(state.calc_type);
        const entry: DeductibleConfigInput = {
            deductible_id: row.deductible_id,
            calc_type: state.calc_type,
            rate: null,
            floor_amount: null,
            cap_amount: null,
            fixed_amount: null,
        };
        for (const field of fields) {
            const raw = state[field].trim();
            entry[field] = raw === "" ? null : raw;
        }
        payload.push(entry);
    }
    return payload;
}

export interface MappedDeductibleErrors {
    rowErrors: DeductibleRowErrors;
    /** Errors on the deductibles array as a whole (for example the 100% total). */
    general: string[];
}

const firstMessage = (messages: unknown): string | undefined => {
    if (Array.isArray(messages)) return messages.find((m) => typeof m === "string");
    return typeof messages === "string" ? messages : undefined;
};

/** Maps `deductibles.{index}.{field}` errors (index into the sent payload) back to deductible rows. */
export function mapDeductibleErrors(
    errors: Record<string, unknown> | null | undefined,
    payload: DeductibleConfigInput[]
): MappedDeductibleErrors {
    const rowErrors: DeductibleRowErrors = {};
    const general: string[] = [];
    for (const [key, messages] of Object.entries(errors ?? {})) {
        const message = firstMessage(messages);
        if (!message) continue;
        if (key === "deductibles") {
            general.push(message);
            continue;
        }
        const match = /^deductibles\.(\d+)\.(\w+)$/.exec(key);
        if (!match) continue;
        const deductibleId = payload[Number(match[1])]?.deductible_id;
        if (deductibleId === undefined) {
            general.push(message);
            continue;
        }
        rowErrors[deductibleId] = { ...rowErrors[deductibleId], [match[2]]: message };
    }
    return { rowErrors, general };
}

const formatAmount = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(value);
};

const formatRate = (rate: number | string | null | undefined): string => {
    if (rate === null || rate === undefined || rate === "") return "—";
    const n = Number(rate);
    return Number.isFinite(n) ? `${n.toLocaleString(undefined, { maximumFractionDigits: 3 })}%` : `${rate}%`;
};

/** Short description of one configuration, for example "10% (floor 100.00, cap 500.00)". */
export function formatDeductibleConfig(c: Pick<DeductibleConfig, "calc_type" | "rate" | "floor_amount" | "cap_amount" | "fixed_amount">): string {
    switch (c.calc_type) {
        case FINANCE_CALC_TYPES.PERCENTAGE:
            return formatRate(c.rate);
        case FINANCE_CALC_TYPES.PERCENTAGE_WITH_CAP:
            return `${formatRate(c.rate)} (cap ${formatAmount(c.cap_amount)})`;
        case FINANCE_CALC_TYPES.PERCENTAGE_WITH_FLOOR_AND_CAP:
            return `${formatRate(c.rate)} (floor ${formatAmount(c.floor_amount)}, cap ${formatAmount(c.cap_amount)})`;
        case FINANCE_CALC_TYPES.FIXED_AMOUNT:
            return `Fixed ${formatAmount(c.fixed_amount)}`;
        default:
            return NO_ALLOCATION_LABEL;
    }
}

export interface DeductibleSummary {
    items: { deductible_id: number; label: string; is_active: boolean }[];
    moreCount: number;
}

/** Table summary: the first `max` configured deductibles in list order, plus how many more. */
export function summarizeDeductibles(configs: DeductibleConfig[] | null | undefined, max: number = 3): DeductibleSummary {
    const sorted = [...(configs ?? [])].sort(byListOrder);
    return {
        items: sorted.slice(0, max).map((c) => ({
            deductible_id: c.deductible_id,
            label: `${c.name}: ${formatDeductibleConfig(c)}`,
            is_active: c.is_active,
        })),
        moreCount: Math.max(0, sorted.length - max),
    };
}

/** Clears a row's amount fields that its (new) calc type does not use. */
export function withCalcType(state: DeductibleRowState | undefined, calcType: DeductibleRowState["calc_type"]): DeductibleRowState {
    const base = state ?? EMPTY_ROW_STATE;
    const keep = fieldsForCalcType(calcType);
    const next: DeductibleRowState = { ...base, calc_type: calcType };
    for (const field of AMOUNT_FIELDS) {
        if (!keep.includes(field)) next[field] = "";
    }
    return next;
}

export const fieldLabel = (field: DeductibleAmountField): string => DEDUCTIBLE_FIELD_LABELS[field];
